import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { OnboardVenue } from '@/types';

interface VenueWelcomeProps {
  githubUsername: string;
  venue: OnboardVenue;
  onContinue: () => void;
}

function formatDateParts(iso: string, timeZone: string) {
  const date = new Date(iso);
  const weekday = new Intl.DateTimeFormat('es-AR', {
    weekday: 'short',
    timeZone,
  })
    .format(date)
    .replace('.', '');
  const day = new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    timeZone,
  }).format(date);
  const month = new Intl.DateTimeFormat('es-AR', {
    month: 'short',
    timeZone,
  })
    .format(date)
    .replace('.', '');
  const time = new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(date);
  return { weekday, day, month, time };
}

export function VenueWelcome({
  githubUsername,
  venue,
  onContinue,
}: VenueWelcomeProps) {
  const start = formatDateParts(venue.startsAt, venue.timezone);
  const end = formatDateParts(venue.endsAt, venue.timezone);

  return (
    <div className="h-[100dvh] overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-2xl flex-col justify-center gap-6 px-6 py-6">
        <a
          href={venue.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block min-h-0 w-full flex-1 overflow-hidden"
        >
          <img
            src="/assets/images/misc/workplace.webp"
            alt="Venue"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-opacity duration-500 group-hover:opacity-90" />
          <img
            src="/hack-26/credentials/logo-hack-26-white.svg"
            alt="Platanus Hack 26"
            width={220}
            height={80}
            className="group-hover:-translate-y-0.5 absolute bottom-5 left-5 h-10 w-auto transition-transform duration-500 ease-out sm:h-12"
          />
          <div className="absolute right-5 bottom-5 flex translate-y-1 items-center gap-1.5 font-mono text-white/0 text-xs uppercase tracking-widest opacity-0 transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:text-white/90 group-hover:opacity-100">
            Ver en mapa
            <span aria-hidden>↗</span>
          </div>
        </a>

        <div className="space-y-1">
          <h1 className="font-semibold text-3xl text-primary tracking-tight sm:text-4xl">
            Todo listo, {githubUsername}?
          </h1>
          <p className="font-mono text-primary/60 text-sm">
            La hack más cool de tu vida te espera.
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <DatePoint parts={start} />
          <ArrowRight
            className="h-5 w-5 shrink-0 text-primary/40"
            strokeWidth={1.5}
          />
          <DatePoint parts={end} align="right" />
        </div>

        <Button size="lg" className="w-full" onClick={onContinue}>
          Vamos 🍌
        </Button>
      </div>
    </div>
  );
}

interface DatePointProps {
  parts: ReturnType<typeof formatDateParts>;
  align?: 'left' | 'right';
}

function DatePoint({ parts, align = 'left' }: DatePointProps) {
  return (
    <div
      className={`flex flex-col gap-0.5 ${
        align === 'right' ? 'items-end text-right' : 'items-start text-left'
      }`}
    >
      <span className="font-mono text-primary/50 text-xs capitalize">
        {parts.weekday} {parts.day} {parts.month}
      </span>
      <span className="font-semibold text-2xl text-primary leading-none">
        {parts.time} hs
      </span>
    </div>
  );
}
